import React, { useEffect, useState } from 'react';
import { ArrowLeft, Save, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { getRateQuoteNotes, insertRateQuoteNotes } from '@/redux/RateQuote/actions';
import { el } from 'date-fns/locale';
import { set } from 'date-fns';

const RateQuoteNote = ({ orderId, orderNum, onClose }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [contactPerson, setContactPerson] = useState('');
    const [isChecked, setIsChecked] = useState(false);
    const [status, setStatus] = useState('');
    const [noteDate, setNoteDate] = useState();
    const [notes, setNotes] = useState('');
    const [noteList, setNoteList] = useState([]);
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const { countryList, documentTypePurchaseList, documentTypeSalesList, incoTermsList, quantityTypeList, salesPersonList, uomTypeList,
        commodityGroupList, containerTypeList, operatorList, moveTypeList, currency_list, charges_basis_list } = useSelector((state) => state.common);

    useEffect(() => {
        // Fetch contact list
        _getNoteList();
    }, [orderId]);

    const _getNoteList = async () => {
        await getRateQuoteNotes(orderId).then(res => {
            setNoteList(res?.data || []);
        });
    }

    const _handleSave = () => {
        if (!contactPerson) {
            alert('Please select a contact person.');
            return;
        }
        if (!status) {
            alert('Please select a status.');
            return;
        }
        if (!notes.trim()) {
            alert('Please enter notes.');
            return;
        }
        const payload = {
            "RateQuoteId": orderId,
            "FileNumber": orderNum,
            "ModuleName": "rATEqUOTE",
            "NoteStatus": status,
            "Notes": notes,
            "Reminder": isChecked,
            "ReminderDate": noteDate ? moment(noteDate).format('YYYY-MM-DD') : null,
            "CreatedBy": authUser?.firstName + ' ' + authUser?.lastName,
            "ContactPerson": contactPerson,
            "NotesType": "INTERNAL",
            "IsActive": true,
        }
        insertRateQuoteNotes(payload).then(res => {
            if (res) {
                _getNoteList();
                setContactPerson('');
                setStatus('');
                setIsChecked(false);
                setNoteDate('');
                setNotes('');
                alert('Note saved successfully.');
            } else {
                alert('Failed to save note. Please try again.');
            }
        }).catch(err => {
            console.error('Error saving note:', err);
            alert('Failed to save note. Please try again.');
        });
    }


    return (
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-6">Rate Quote Notes</h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Contact Person */}
                <div className=''>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                        To Contact Person <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={contactPerson}
                        onChange={(e) => setContactPerson(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-[#0072FF] focus:border-[#0072FF]"
                    >
                        <option value="">SELECT</option>
                        {salesPersonList.map((person, index) => (
                            <option key={index} value={person.salesPerson}>{person.salesPerson}</option>
                        ))}
                    </select>
                </div>

                {/* Status */}
                <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                        Note Status <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-[#0072FF] focus:border-[#0072FF]"
                    >
                        <option value="">SELECT</option>
                        <option value="HIGH">High</option>
                        <option value="MEDIUM">Medium</option>
                        <option value="LOW">Low</option>
                    </select>
                </div>

                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="followUp"
                        checked={isChecked}
                        onChange={(e) => { setIsChecked(e.target.checked); if (e.target.checked) setNoteDate(moment().format('YYYY-MM-DD')); }}
                        className="w-4 h-4 text-[#0072FF] border-slate-300 dark:border-slate-700 rounded focus:ring-[#0072FF]"
                    />
                    <label htmlFor="followUp" className="ml-2 text-sm font-medium text-slate-600 dark:text-slate-400">Set Reminder</label>
                </div>

                {/* Date Field */}
                <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Reminder Date</label>
                    <input
                        type="date"
                        disabled={!isChecked}
                        value={noteDate}
                        min={moment().format('YYYY-MM-DD')}
                        onChange={(e) => setNoteDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-[#0072FF] focus:border-[#0072FF]"
                    />
                </div>

                {/* Checkbox */}

            </div>

            {/* Notes Textarea */}
            <div className="mt-6">
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                    Notes <span className="text-red-500">*</span>
                </label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Enter your notes here..."
                    rows="5"
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-[#0072FF] focus:border-[#0072FF] resize-none"
                />
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex gap-3 justify-end">
                <button
                    onClick={_handleSave}
                    className="px-6 py-2 text-sm bg-[#0072FF] hover:bg-[#0062DD] text-white rounded-lg font-medium transition-all flex items-center gap-2"
                >
                    <Save className="w-4 h-4" />
                    Save Note
                </button>
                <button
                    onClick={() => onClose()}
                    className="px-6 py-2 text-sm bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-900 dark:text-white rounded-lg font-medium transition-all flex items-center gap-2"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Cancel
                </button>
            </div>

            <div className='mt-10'>
                <table className="w-full text-xs ev_ar_table">
                    <thead>
                        <tr className="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-800">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">To Contact Person</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Notes</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Created By</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Created Date</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {
                            noteList.length > 0 ? noteList.map((note, index) => (
                                <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{note.ContactPerson}</td>
                                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{note.NoteStatus}</td>
                                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{note.Notes}</td>
                                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{note.CreatedBy}</td>
                                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{moment(note.CreatedOn).format('DD MMM YYYY')}</td>
                                </tr>
                            ))
                                :
                                <tr>
                                    <td colSpan="5" className="px-4 py-3 text-center text-slate-600 dark:text-slate-400">No Notes Available.</td>
                                </tr>
                        }
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default RateQuoteNote;